import * as React from 'react'
import { useEffect } from 'react'

import {
  Button,
  CheckCircleIcon,
  DropdownMenu,
  EllipsisVerticalIcon,
  FileTextIcon,
  Flex,
  IconButton,
  IdeasIcon,
} from '@buffer-mono/popcorn'

import { useIdeaComposer } from '~publish/components/IdeaManagementRouter'
import { NewPostComposerTrigger } from '~publish/components/NewPostComposerTrigger'
import type { Channel } from '~publish/gql/graphql'

import type { FilterStatus } from '../CommentsFilters/useFilters'
import { triggerEngagementScoreStart } from '../CommentsList/utils/commentScoreSessionManager'
import { useCommentData } from '../context/CommentContext'
import { useDismissComment } from '../hooks/useDismissComment'
import { useUndismissComment } from '../hooks/useUndismissComment'

import styles from './CommentActions.module.css'

export const CommentActionsMenu = ({
  channelId,
}: {
  channelId: string
}): JSX.Element => {
  const [isOpen, setIsOpen] = React.useState(false)
  const { createIdeaWithComposer } = useIdeaComposer()
  const comment = useCommentData()

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenu.Trigger>
        <Button size="medium" variant="tertiary">
          <EllipsisVerticalIcon />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content data-no-focus-lock className={styles.optionsMenu}>
        <DropdownMenu.Group label="Feeling inspired by this comment?">
          <NewPostComposerTrigger
            cta="publish-queue-emptyState-newPost-1"
            channels={[channelId]}
            prefillPostData={{
              text: comment.text,
              media:
                comment.assets.length > 0
                  ? {
                      thumbnail: comment.assets[0].thumbnail,
                      source: comment.assets[0].source,
                      picture: comment.assets[0].source,
                      // TODO: Review if we should use the actual width and height of the image
                      // This is currently hardcoded because the types are mandatory and we don't have a way to get the actual width and height of the image
                      width: '',
                      height: '',
                      progress: '100',
                      uploaded: 'true',
                    }
                  : undefined,
            }}
          >
            <DropdownMenu.Item
              onClick={(): void => {
                setIsOpen(false)
              }}
            >
              <FileTextIcon
                size="medium"
                color="brand"
                className={styles.postIcon}
              />
              Start a Post
            </DropdownMenu.Item>
          </NewPostComposerTrigger>
          <DropdownMenu.Item
            onClick={(): void => {
              setIsOpen(false)

              createIdeaWithComposer({
                source: 'publish-comments-commentItem-createIdea-1',
                text: comment.text,
              })
            }}
          >
            <IdeasIcon
              size="medium"
              color="success"
              className={styles.ideaIcon}
            />
            Create an Idea
          </DropdownMenu.Item>
        </DropdownMenu.Group>
      </DropdownMenu.Content>
    </DropdownMenu>
  )
}

type CommentActionsProps = {
  channel: Channel
  currentStatusFilter?: FilterStatus
  setShouldAnimateOut: (shouldAnimateOut: boolean) => void
  onLoadingChange?: (isLoading: boolean) => void
  disabled?: boolean
}

export const CommentActions = ({
  channel,
  currentStatusFilter,
  setShouldAnimateOut,
  onLoadingChange,
  disabled = false,
}: CommentActionsProps): JSX.Element => {
  const comment = useCommentData()
  const { dismissComment, loading: isDismissingComment } = useDismissComment()
  const { undismissComment, loading: isUndismissingComment } =
    useUndismissComment()

  const isLoading = isDismissingComment || isUndismissingComment

  // Notify parent of loading state changes
  useEffect(() => {
    onLoadingChange?.(isLoading)
  }, [isLoading, onLoadingChange])

  const handleDismissComment = async (): Promise<void> => {
    if (currentStatusFilter === 'unreplied') {
      setShouldAnimateOut(true)
    }
    await dismissComment(comment.id)
    triggerEngagementScoreStart(channel.id)
  }

  const handleUndoDismissComment = async (): Promise<void> => {
    if (currentStatusFilter === 'dismissed') {
      setShouldAnimateOut(true)
    }
    await undismissComment(comment.id)
    triggerEngagementScoreStart(channel.id)
  }

  // Wrapper functions to handle async event handlers
  const handleDismissClick = (): void => {
    void handleDismissComment()
  }

  const handleUndoDismissClick = (): void => {
    void handleUndoDismissComment()
  }

  const finalDisabled = disabled || isLoading

  return (
    <Flex>
      {comment.status === 'dismissed' && (
        <IconButton
          label="Reopen this comment"
          onClick={handleUndoDismissClick}
          tooltip="Reopen this comment"
          variant="tertiary"
          className={styles.undoDismissButton}
          disabled={finalDisabled}
          aria-label="Reopen this comment"
        >
          <CheckCircleIcon />
        </IconButton>
      )}
      {(comment.status === 'unreplied' || comment.status === 'new') && (
        <IconButton
          label="Resolve this comment"
          onClick={handleDismissClick}
          tooltip="Resolve this comment"
          variant="tertiary"
          className={styles.dismissButton}
          disabled={finalDisabled}
          name="dismiss-comment"
          aria-label="Resolve this comment"
        >
          <CheckCircleIcon />
        </IconButton>
      )}
      <CommentActionsMenu channelId={channel.id} />
    </Flex>
  )
}
